import * as React from 'react';
import { Button } from 'Src/components/Button/Button';
import { FormRendererV2 } from 'Src/components/FormRendererV2/FormRendererV2';
import { SendTimeFormEditor } from 'Src/components/SendTimeFormEditor/SendTimeFormEditor';
import { AppUser } from 'Src/views/App/App';
import { updateProgrammeClub } from 'Src/helpers/service/admin/clubProgrammes';
import { validateFormDefinitionStructure } from 'Src/helpers/form/formDefinitionValidation';
import { uploadFormFile, uploadFormImage } from 'Src/helpers/form/formMedia';
import { formDefinitionToLegacyFields, legacyFieldsToFormDefinition } from 'Src/helpers/form/legacyFormAdapter';
import { FormDefinition } from 'Src/types/formSchema';
import { ProgrammeField } from './helpers/clubProgrammeFieldsOverrideEditor';

interface Props {
  user: AppUser;
  programmeId: string;
  programmeClubId: string;
  programmeDefaultFields?: ProgrammeField[];
  programmeFormDefinition?: FormDefinition;
  currentOverride?: {
    mode?: 'REPLACE' | 'MERGE';
    fields?: ProgrammeField[];
    formSchemaVersion?: 1 | 2;
    formDefinition?: FormDefinition;
  } | null | undefined;
  canEdit: boolean;
  onSaved: (updatedProgramme: any) => void;
  onClose: () => void;
  defaultTemplateName?: string;
}

const getDefinition = (props: Pick<Props, 'programmeDefaultFields' | 'programmeFormDefinition' | 'currentOverride'>): FormDefinition => {
  const override = props.currentOverride;

  if (Number(override?.formSchemaVersion) === 2 && override?.formDefinition) {
    return override.formDefinition;
  }

  if (Array.isArray(override?.fields) && override!.fields!.length > 0) {
    return legacyFieldsToFormDefinition(override!.fields! as any, {
      title: 'Club booking form override'
    });
  }

  if (props.programmeFormDefinition) {
    return props.programmeFormDefinition;
  }

  return legacyFieldsToFormDefinition((props.programmeDefaultFields || []) as any, {
    title: 'Club booking form override'
  });
};

export const ClubProgrammeClubFieldsOverrideEditor: React.FC<Props> = props => {
  const {
    user,
    programmeId,
    programmeClubId,
    programmeDefaultFields = [],
    programmeFormDefinition,
    currentOverride,
    canEdit,
    onSaved,
    onClose,
    defaultTemplateName = 'Club booking template'
  } = props;

  const [definition, setDefinition] = React.useState<FormDefinition>(() =>
    getDefinition({ programmeDefaultFields, programmeFormDefinition, currentOverride })
  );
  const [savedSnapshot, setSavedSnapshot] = React.useState(() => JSON.stringify(definition));
  const [previewAnswers, setPreviewAnswers] = React.useState({});
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const isDirty = JSON.stringify(definition) !== savedSnapshot;

  React.useEffect(() => {
    const next = getDefinition({ programmeDefaultFields, programmeFormDefinition, currentOverride });
    setDefinition(next);
    setSavedSnapshot(JSON.stringify(next));
    setPreviewAnswers({});
    setError('');
  }, [programmeClubId, JSON.stringify(currentOverride || {}), JSON.stringify(programmeDefaultFields || []), JSON.stringify(programmeFormDefinition || {})]);

  React.useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const validate = (): boolean => {
    const issues = validateFormDefinitionStructure(definition);
    if (issues.length > 0) {
      setError(issues.map(issue => issue.message).join(' '));
      return false;
    }

    const invalidElement = definition.elements.find(element => {
      if (element.type === 'DIVIDER' || element.type === 'PAGE_BREAK') return false;
      return !String(element.title || '').trim();
    });

    if (invalidElement) {
      setError('Every question and content element must have a title.');
      return false;
    }

    return true;
  };

  const save = () => {
    if (!canEdit || isSaving) return;
    if (!programmeId || !programmeClubId) {
      setError('Programme/club id is missing.');
      return;
    }
    if (!validate()) return;

    const fields = formDefinitionToLegacyFields(definition);
    setIsSaving(true);
    setError('');

    updateProgrammeClub(user, programmeId, programmeClubId, {
      fieldsOverride: {
        mode: 'REPLACE',
        formSchemaVersion: 2,
        formDefinition: definition,
        fields: fields as any
      }
    })
      .then((updated: any) => {
        setSavedSnapshot(JSON.stringify(definition));
        setIsSaving(false);
        if (updated) onSaved(updated);
        onClose();
      })
      .catch((e: any) => {
        const text = e?.response?.data?.details?.text || 'Failed to save override fields.';
        setError(String(text || 'Failed to save override fields.'));
        setIsSaving(false);
      });
  };

  if (!canEdit) {
    return (
      <>
        <div className="alert alert-light border">
          This programme is not in DRAFT status. Override fields are locked to preserve existing invitations and
          submitted answers.
        </div>
        <FormRendererV2
          definition={definition}
          answers={previewAnswers}
          onChange={setPreviewAnswers}
          onSubmitRequested={() => undefined}
        />
      </>
    );
  }

  return (
    <>
      {error ? <div className="alert alert-danger">{error}</div> : null}

      <SendTimeFormEditor
        definition={definition}
        onChange={next => {
          setDefinition(next);
          setError('');
        }}
        uploadImage={file => uploadFormImage(user, file)}
        uploadFile={file => uploadFormFile(user, file)}
        user={user}
        templateType="CLUB_BOOKING"
        defaultTemplateName={defaultTemplateName}
        defaultBuilderMode="FULL"
        showTemplateSaveAction={true}
        templateSaveButtonText="Save as club booking template"
        selectDefaultTemplateOnLoad={false}
        emptyTemplateLabel="Current club form / start from scratch"
      />

      <div className="d-flex justify-content-end mt-3">
        <Button
          text="Discard club form changes"
          customClass="btn-secondary mr-2"
          disabled={!isDirty || isSaving}
          onClick={() => {
            const restored = JSON.parse(savedSnapshot) as FormDefinition;
            setDefinition(restored);
            setError('');
          }}
        />
        <Button
          text={isSaving ? 'Saving...' : 'Save club booking form'}
          disabled={!isDirty || isSaving}
          onClick={save}
        />
      </div>
    </>
  );
};